import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { z } from "zod";

import {
  AnyProductType,
  productTypeAccessory,
  productTypeCombo,
  productTypePackage,
  productTypeTire,
  productTypeWheel,
} from "../../constants/productTypes";
import {
  AllDealerRoutes,
  DealerVehicleResultsRoute,
} from "../../navigation/DealerRoutes";
import {
  AllNationalRoutes,
  NationalVehicleResultsRoute,
} from "../../navigation/NationalRoutes";
import { useRouteParams } from "../../navigation/useRouteParams";
import { useSomeParams } from "../../navigation/useSomeParams";
import { nullable } from "../../util/nullable";
import { reduxStore } from "../reduxStore";
import { searchFormSlice } from "../slices/searchFormSlice";
import { useSearchForm } from "./useSearchForm";

const SearchFormSchema = z.object({
  dealerId: z.string().optional(),
  partNumber: z.string().optional(),
  postalCode: z.string().optional(),
  productType: z
    .enum([
      productTypeTire,
      productTypeCombo,
      productTypeAccessory,
      productTypePackage,
      productTypeWheel,
    ])
    .optional(),
  tireSize: z.string().optional(),
  vehicleManufacturerName: z.string().optional(),
  vehicleModelClassId: z.string().optional(),
  vehicleModelName: z.string().optional(),
  vehicleSubmodel: z.string().optional(),
  vehicleSubmodelTiresizeId: z.string().nullable().optional(),
  vehicleYear: z.string().optional(),
});

export const useSearchFormSync = (): void => {
  const [{ vehicleModelClassId }] = useSearchForm();
  const dispatch = useDispatch();
  const dealerVehiclyResultsRoute = useRouteParams(DealerVehicleResultsRoute);
  const nationalVehicleResultsRoute = useRouteParams(
    NationalVehicleResultsRoute
  );
  const vehicleResultsRoute =
    dealerVehiclyResultsRoute || nationalVehicleResultsRoute;

  const someParams = useSomeParams(...AllDealerRoutes, ...AllNationalRoutes);

  // Restore product type from URL
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const parsedState = SearchFormSchema.safeParse({
      ...someParams,
      vehicleSubmodelTiresizeId: searchParams.get("size"),
    });

    if (parsedState.success) {
      const action = searchFormSlice.actions.patch({
        ...parsedState.data,

        vehicleModelClassId: nullable(
          vehicleModelClassId ?? parsedState.data.vehicleModelClassId
        ),
        vehicleSubmodelSelected: someParams.vehicleSubmodel || null,
      });
      dispatch(action);
    }
  }, []);

  // Sync product type with product route
  useEffect(() => {
    const { productType } = reduxStore.getState().searchForm;
    if (
      vehicleResultsRoute &&
      productType !== vehicleResultsRoute.productType
    ) {
      const action = searchFormSlice.actions.updateProductType(
        vehicleResultsRoute.productType as AnyProductType
      );
      dispatch(action);
    }
  }, [vehicleResultsRoute?.productType]);
};
